import { renderSVG } from 'uqr';
import { guestApi } from '../api/guest';
import type {
  SedrAuditEvent,
  SedrFile,
  SedrGuestRoomContext,
  SedrPhasesList,
  SedrRequirement,
} from '../api/sedr';
import { downloadAndDecryptSedrEvidence, sedrEvidenceNeedsRoomKey } from '../crypto/sedrEvidenceFile';
import { unwrapSedrRoomKey } from '../crypto/sedrRoomKey';
import { formatFileSize } from '../crypto/sharedFolderFile';
import { applyI18n, t } from '../i18n';
import { bindLocaleRefresh } from '../i18n/bindLocaleRefresh';
import {
  buildGuestSedrRoomUrl,
  pushAppUrl,
  readGuestSedrRoomUrlState,
  type GuestSedrRoomTab,
} from '../app/navigationState';
import { setCryptoBusy } from '../ui/cryptoBusy';
import { showFilePreview } from '../ui/filePreviewDialog';
import { setGuestShellName } from '../ui/guestShellName';
import { renderSedrEvidenceExplorer } from './sedr/sedrEvidenceExplorer';
import {
  renderSedrCompliancePanel,
  type SedrCompliancePanelController,
} from './sedr/sedrCompliancePanel';
import {
  renderSedrDashboard,
  type CompliancePhaseId,
} from './sedr/sedrDashboard';
import {
  isSedrLifecycleClosed,
  renderRoomStatusBanner,
  seedSedrCategoriesCache,
  type SedrCategoryFilter,
  type SedrEvidenceFilter,
  type SedrLifecycleRequestFilter,
  type SedrMetricKind,
  type SedrOwnerFilter,
  type SedrRenewalFilter,
  type SedrStatusFilter,
} from './sedr/sedrUi';
import {
  buildSedrAuditLookup,
  mountSedrAuditExpandableTable,
} from './sedr/sedrAuditDetail';
import { createHelpTip } from '../help/helpTips';
import { sedrTabHelpTopic } from '../help/defaultTopic';
import {
  applySidebarCollapsed,
  createActivePanelControl,
  effectiveSidebarCollapsed,
  readSidebarCollapsed,
  renderSedrRoomChromeActions,
  SEDR_MOBILE_LAYOUT_MQ,
  writeSidebarCollapsed,
  type SedrChromeTab,
} from './sedr/sedrRoomChrome';

type SedrGuestTab = GuestSedrRoomTab;

function guestErrorKey(cause: unknown): string {
  const code = cause instanceof Error ? cause.message : '';
  const statusCode = (cause as { code?: string }).code ?? code;
  switch (statusCode) {
    case 'access_denied':
    case 'room_not_found':
      return 'sedr.guest.revoked';
    case 'incorrect_totp':
      return 'sedr.guest.incorrectTotp';
    case 'totp_pending_expired':
      return 'sedr.guest.totpPendingExpired';
    case 'totp_enrollment_required':
      return 'sedr.guest.totpEnrollHint';
    case 'totp_verification_required':
      return 'sedr.guest.totpChallengeHint';
    case 'vault_locked':
      return 'guest.unlock.title';
    default:
      return 'status.error';
  }
}

export function GuestAccountSedrPage(roomId: string): HTMLElement {
  const page = document.createElement('section');
  page.className = 'module-page module-page--sedr';
  page.dataset.guestAccountSedr = '';

  void guestApi.session()
    .then((s) => setGuestShellName(s.displayName, s.organization))
    .catch(() => undefined);

  let verified = false;
  let ctx: SedrGuestRoomContext | null = null;
  let roomKey: Uint8Array | null = null;
  const initialUrl = readGuestSedrRoomUrlState();
  const urlMatchesRoom = initialUrl?.roomId === roomId;
  let activeTab: SedrGuestTab = urlMatchesRoom ? initialUrl!.tab : 'dashboard';
  let complianceStatusFilter: SedrStatusFilter = [];
  let complianceCategoryFilter: SedrCategoryFilter = 'all';
  let compliancePhaseId: CompliancePhaseId = null;
  let complianceSearch = '';
  let complianceCommentSearch = '';
  let complianceHideObsolete = true;
  let complianceObsoleteSubtypes: SedrStatusFilter | undefined;
  let complianceOwnerFilter: SedrOwnerFilter = 'all';
  let complianceRenewalFilter: SedrRenewalFilter = 'all';
  let complianceLifecycleRequestFilter: SedrLifecycleRequestFilter = 'all';
  let complianceEvidenceFilter: SedrEvidenceFilter = 'all';
  let selectedRequirementId: string | null = urlMatchesRoom ? initialUrl!.requirementId : null;
  let selectedRequirementIds: string[] = selectedRequirementId ? [selectedRequirementId] : [];
  let requirements: SedrRequirement[] = [];
  let phasesList: SedrPhasesList = { phases: [], unassignedRequirementCount: 0 };
  let files: SedrFile[] = [];
  let auditEvents: SedrAuditEvent[] = [];
  let evidenceBusy = false;
  let evidenceParentId: string | null = urlMatchesRoom ? initialUrl!.evidenceFolderId : null;
  let evidenceSearch = '';
  let compliancePanel: SedrCompliancePanelController | null = null;
  let sidebarPreference = readSidebarCollapsed();
  let chromeActionsCleanup: (() => void) | null = null;
  const sidebarMq = window.matchMedia(SEDR_MOBILE_LAYOUT_MQ);
  const isSidebarCollapsed = () => effectiveSidebarCollapsed(sidebarPreference);

  page.innerHTML = `
    <div class="module-section" id="sedr-guest-gate">
      <h2 data-i18n="sedr.guest.title"></h2>
      <p class="muted" data-i18n="sedr.guest.subtitle"></p>
      <div id="sedr-guest-totp-enroll" class="form-grid" hidden>
        <p class="muted" data-i18n="sedr.guest.totpEnrollHint"></p>
        <div id="sedr-guest-totp-qr" class="sedr-guest-totp-qr" aria-hidden="true"></div>
        <label>
          <span data-i18n="sedr.guest.totpSecret"></span>
          <input type="text" id="sedr-guest-totp-secret" readonly autocomplete="off" />
        </label>
        <form id="sedr-guest-totp-enroll-form" class="form-grid">
          <label>
            <span data-i18n="sedr.guest.totpCode"></span>
            <input type="text" name="totpCode" inputmode="numeric" autocomplete="one-time-code" maxlength="8" required />
          </label>
          <button type="submit" class="button primary" data-i18n="sedr.guest.totpConfirm"></button>
        </form>
      </div>
      <form id="sedr-guest-totp-challenge" class="form-grid" hidden>
        <p class="muted" data-i18n="sedr.guest.totpChallengeHint"></p>
        <label>
          <span data-i18n="sedr.guest.totpCode"></span>
          <input type="text" name="totpCode" inputmode="numeric" autocomplete="one-time-code" maxlength="8" required />
        </label>
        <button type="submit" class="button primary" data-i18n="sedr.guest.enter"></button>
      </form>
      <p class="error-text" id="sedr-guest-error" hidden></p>
      <p class="muted" id="sedr-guest-status" hidden></p>
    </div>
  `;
  applyI18n(page);
  page.querySelector('#sedr-guest-gate h2')?.append(createHelpTip('guest-sedr-dashboard'));

  const gateSection = page.querySelector('#sedr-guest-gate') as HTMLElement;
  const enrollPanel = page.querySelector('#sedr-guest-totp-enroll') as HTMLElement;
  const enrollForm = page.querySelector('#sedr-guest-totp-enroll-form') as HTMLFormElement;
  const challengeForm = page.querySelector('#sedr-guest-totp-challenge') as HTMLFormElement;
  const qrHost = page.querySelector('#sedr-guest-totp-qr') as HTMLElement;
  const secretInput = page.querySelector('#sedr-guest-totp-secret') as HTMLInputElement;
  const errorEl = page.querySelector('#sedr-guest-error') as HTMLParagraphElement;
  const statusEl = page.querySelector('#sedr-guest-status') as HTMLParagraphElement;

  let entering = false;
  let errorKey: string | null = null;
  let statusKey: string | null = null;
  let pendingToken: string | null = null;
  let requireGuestTotp = false;

  const busyOptions = {
    titleKey: 'sedr.guest.entering',
    detailKey: 'sedr.guest.enteringDetail',
  };

  const hideGateForms = () => {
    enrollPanel.hidden = true;
    challengeForm.hidden = true;
  };

  const showEnroll = (secret: string | null, otpauthUri: string | null, token: string) => {
    hideGateForms();
    pendingToken = token;
    secretInput.value = secret ?? '';
    qrHost.innerHTML = '';
    if (otpauthUri) {
      qrHost.innerHTML = renderSVG(otpauthUri, { ecc: 'M', pixelSize: 4 });
    }
    enrollPanel.hidden = false;
    const codeInput = enrollForm.querySelector('input[name="totpCode"]') as HTMLInputElement;
    codeInput.value = '';
    codeInput.focus();
  };

  const showChallenge = () => {
    hideGateForms();
    challengeForm.hidden = false;
    const codeInput = challengeForm.querySelector('input[name="totpCode"]') as HTMLInputElement;
    codeInput.value = '';
    codeInput.focus();
  };

  const showError = (key: string) => {
    errorKey = key;
    statusKey = null;
    errorEl.textContent = t(key);
    errorEl.hidden = false;
    statusEl.hidden = true;
  };

  const layoutEls = () => ({
    layout: page.querySelector('.sedr-room-layout') as HTMLElement | null,
    sidebar: page.querySelector('#sedr-sidebar') as HTMLElement | null,
    titleEl: page.querySelector('#sedr-room-title') as HTMLElement | null,
    orgEl: page.querySelector('#sedr-room-org') as HTMLElement | null,
    panelSlot: page.querySelector('#sedr-room-panel-slot') as HTMLElement | null,
    bannerSlot: page.querySelector('#sedr-room-banner-slot') as HTMLElement | null,
    actionsSlot: page.querySelector('#sedr-room-actions') as HTMLElement | null,
    tabContent: page.querySelector('#sedr-tab-content') as HTMLElement,
  });

  const getNavTabs = (): SedrChromeTab[] => [
    { id: 'dashboard', labelKey: 'sedr.nav.dashboard' },
    { id: 'compliance', labelKey: 'sedr.nav.compliance' },
    { id: 'evidence', labelKey: 'sedr.nav.evidence' },
    { id: 'audit', labelKey: 'sedr.nav.audit' },
  ];

  const navigateRoom = (
    tab: SedrGuestTab,
    options?: { requirementId?: string | null; evidenceFolderId?: string | null; replace?: boolean },
  ) => {
    activeTab = tab;
    if (options?.requirementId !== undefined) {
      selectedRequirementId = options.requirementId;
      selectedRequirementIds = selectedRequirementId ? [selectedRequirementId] : [];
      if (tab === 'compliance') {
        compliancePanel = null;
      }
    }
    if (options?.evidenceFolderId !== undefined) {
      evidenceParentId = options.evidenceFolderId;
    }
    pushAppUrl(
      buildGuestSedrRoomUrl(roomId, tab, {
        requirementId: tab === 'compliance' ? selectedRequirementId : null,
        evidenceFolderId: tab === 'evidence' ? evidenceParentId : null,
      }),
      { replace: options?.replace },
    );
    renderSidebar();
    updateRoomHelpTip();
    void renderTab();
  };

  const applyUrlState = () => {
    const urlState = readGuestSedrRoomUrlState();
    if (!urlState || urlState.roomId !== roomId) {
      return;
    }
    if (
      urlState.tab === activeTab
      && urlState.requirementId === selectedRequirementId
      && urlState.evidenceFolderId === evidenceParentId
    ) {
      return;
    }
    if (
      urlState.tab === 'compliance'
      && (urlState.tab !== activeTab || urlState.requirementId !== selectedRequirementId)
    ) {
      compliancePanel = null;
    }
    activeTab = urlState.tab;
    selectedRequirementId = urlState.requirementId;
    selectedRequirementIds = selectedRequirementId ? [selectedRequirementId] : [];
    evidenceParentId = urlState.evidenceFolderId;
    renderSidebar();
    updateRoomHelpTip();
    void renderTab();
  };

  const onRoomNav = () => applyUrlState();
  page.addEventListener('guest-sedr:room-nav', onRoomNav);

  const selectNavTab = (tabId: string) => {
    navigateRoom(tabId as SedrGuestTab);
  };

  const renderSidebar = () => {
    const { layout, sidebar } = layoutEls();
    if (!layout || !sidebar) return;
    const sidebarCollapsed = isSidebarCollapsed();
    applySidebarCollapsed(layout, sidebar, sidebarCollapsed);
    if (sidebarCollapsed) return;

    const tabs = getNavTabs();
    sidebar.innerHTML = `
      <h2 class="sedr-sidebar__heading" data-i18n="sedr.nav.heading"></h2>
      <nav class="sedr-sidebar-nav" data-i18n-aria-label="sedr.nav.heading"></nav>
    `;
    const nav = sidebar.querySelector('.sedr-sidebar-nav') as HTMLElement;
    for (const tab of tabs) {
      const link = document.createElement('button');
      link.type = 'button';
      link.className = `sedr-sidebar-link${activeTab === tab.id ? ' sedr-sidebar-link--active' : ''}`;
      link.dataset.tab = tab.id;
      link.setAttribute('data-i18n', tab.labelKey);
      link.addEventListener('click', () => selectNavTab(tab.id));
      nav.append(link);
    }
    applyI18n(sidebar);
  };

  const updateRoomHelpTip = () => {
    const { titleEl, panelSlot, actionsSlot } = layoutEls();
    const topic = sedrTabHelpTopic(activeTab, { guest: true });
    page.setAttribute('data-help-context', topic);
    if (!titleEl || !ctx) return;
    const sidebarCollapsed = isSidebarCollapsed();
    titleEl.textContent = ctx.title;
    if (panelSlot) {
      panelSlot.replaceChildren(createActivePanelControl({
        activeTab,
        tabs: getNavTabs(),
        navHidden: sidebarCollapsed,
        onSelect: selectNavTab,
      }));
      applyI18n(panelSlot);
    }
    if (actionsSlot) {
      chromeActionsCleanup?.();
      const { fragment, cleanup } = renderSedrRoomChromeActions({
        fullscreenTarget: page,
        sidebarCollapsed,
        onToggleSidebar: () => {
          sidebarPreference = !sidebarPreference;
          writeSidebarCollapsed(sidebarPreference);
          renderSidebar();
          updateRoomHelpTip();
        },
        helpTopic: topic,
      });
      chromeActionsCleanup = cleanup;
      actionsSlot.replaceChildren(fragment);
      applyI18n(actionsSlot);
    }
  };

  const renderHeader = () => {
    const { titleEl, orgEl, bannerSlot } = layoutEls();
    if (!titleEl || !ctx) return;
    updateRoomHelpTip();
    if (orgEl) {
      orgEl.textContent = ctx.clientOrganization ? `· ${ctx.clientOrganization}` : '';
      orgEl.hidden = !ctx.clientOrganization;
    }
    if (bannerSlot) {
      bannerSlot.replaceChildren();
      const banner = renderRoomStatusBanner(ctx);
      if (banner) bannerSlot.append(banner);
    }
  };

  const guestComplianceOptions = () => ({
    accountRoomId: roomId,
    canComment: Boolean(ctx?.allowCommenting),
    canApprove: Boolean(ctx?.canApprove),
  });

  const renderCompliance = () => {
    const { tabContent } = layoutEls();
    if (!ctx || !tabContent) return;
    if (selectedRequirementId) {
      const selected = requirements.find((r) => r.id === selectedRequirementId);
      if (selected && isSedrLifecycleClosed(selected.status)) {
        complianceHideObsolete = false;
      }
    }
    if (compliancePanel?.element.isConnected) {
      compliancePanel.update({
        requirements,
        phases: phasesList.phases,
        files,
      });
      return;
    }
    compliancePanel = renderSedrCompliancePanel({
      roomId: ctx.roomId,
      roomTitle: ctx.title,
      clientOrganization: ctx.clientOrganization,
      requirements,
      phases: phasesList.phases,
      files,
      canWrite: false,
      canAdmin: false,
      showExport: true,
      showCreate: false,
      showImport: false,
      initial: {
        statusFilter: complianceStatusFilter,
        categoryFilter: complianceCategoryFilter,
        phaseId: compliancePhaseId,
        search: complianceSearch,
        commentSearch: complianceCommentSearch,
        hideObsolete: complianceHideObsolete,
        obsoleteSubtypes: complianceObsoleteSubtypes,
        ownerFilter: complianceOwnerFilter,
        renewalFilter: complianceRenewalFilter,
        lifecycleRequestFilter: complianceLifecycleRequestFilter,
        evidenceFilter: complianceEvidenceFilter,
        selectedRequirementIds: [...selectedRequirementIds],
      },
      onStateChange: (state) => {
        complianceStatusFilter = state.statusFilter;
        complianceCategoryFilter = state.categoryFilter;
        compliancePhaseId = state.phaseId;
        complianceSearch = state.search;
        complianceCommentSearch = state.commentSearch;
        complianceHideObsolete = state.hideObsolete;
        complianceObsoleteSubtypes = state.obsoleteSubtypes;
        complianceOwnerFilter = state.ownerFilter;
        complianceRenewalFilter = state.renewalFilter;
        complianceLifecycleRequestFilter = state.lifecycleRequestFilter;
        complianceEvidenceFilter = state.evidenceFilter;
        selectedRequirementIds = [...state.selectedRequirementIds];
        const nextId = state.selectedRequirementIds.length === 1
          ? state.selectedRequirementIds[0]!
          : null;
        const reqChanged = nextId !== selectedRequirementId;
        selectedRequirementId = nextId;
        if (reqChanged && activeTab === 'compliance') {
          pushAppUrl(
            buildGuestSedrRoomUrl(roomId, 'compliance', { requirementId: selectedRequirementId }),
          );
        }
      },
      onOpenFile: (file, context) => openFile(file, undefined, context),
      onDownloadFile: (file) => downloadFile(file),
      onRequirementsChanged: async () => {
        requirements = await guestApi.listRequirements(roomId);
        phasesList = await guestApi.listPhases(roomId);
        files = await guestApi.listFiles(roomId);
        renderCompliance();
      },
      onOpenRequirement: (requirementId) => {
        openCompliance({
          statusFilter: [],
          categoryFilter: 'all',
          requirementId,
          phaseId: null,
        });
      },
      guest: guestComplianceOptions(),
    });
    tabContent.replaceChildren(compliancePanel.element);
  };

  const openEvidenceBytes = async (file: SedrFile): Promise<Blob> => {
    const key = sedrEvidenceNeedsRoomKey(file) ? roomKey : null;
    if (sedrEvidenceNeedsRoomKey(file) && !key) throw new Error('room_key_missing');
    const bytes = await downloadAndDecryptSedrEvidence(file, key);
    const copy = new Uint8Array(bytes);
    const buffer = copy.buffer.slice(copy.byteOffset, copy.byteOffset + copy.byteLength) as ArrayBuffer;
    return new Blob([buffer], { type: file.contentType || 'application/octet-stream' });
  };

  const openFile = async (
    file: SedrFile,
    onError?: (message: string) => void,
    context?: { gallery: SedrFile[] },
  ) => {
    if (!file.blobId || !ctx) return;
    try {
      await guestApi.recordFileOpened(roomId, file.id);
      const blob = await openEvidenceBytes(file);
      const gallery = context?.gallery?.filter((f) => Boolean(f.blobId));
      if (gallery && gallery.length > 1) {
        const initialIndex = Math.max(0, gallery.findIndex((f) => f.id === file.id));
        await showFilePreview(file.name, blob, file.contentType, {
          items: gallery.map((f) => ({
            id: f.id,
            fileName: f.name,
            contentType: f.contentType,
          })),
          initialIndex,
          loadItem: async (item) => {
            const target = gallery.find((f) => f.id === item.id);
            if (!target?.blobId) throw new Error('missing_gallery_file');
            await guestApi.recordFileOpened(roomId, target.id);
            return openEvidenceBytes(target);
          },
        });
        return;
      }
      await showFilePreview(file.name, blob, file.contentType);
    } catch (cause) {
      console.error('SEDR account guest preview failed', cause);
      onError?.(t('status.error'));
    }
  };

  const downloadFile = async (file: SedrFile, onError?: (message: string) => void) => {
    if (!file.blobId) return;
    try {
      const blob = await openEvidenceBytes(file);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.name;
      document.body.append(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (cause) {
      console.error('SEDR account guest download failed', cause);
      onError?.(t('status.error'));
    }
  };

  const renderEvidence = () => {
    const { tabContent } = layoutEls();
    const panel = document.createElement('div');
    panel.className = 'sedr-tab-panel sedr-tab-panel--evidence';

    const showEvidenceError = (message: string) => {
      const err = panel.querySelector('#sedr-evidence-error') as HTMLParagraphElement | null;
      if (err) {
        err.hidden = false;
        err.textContent = message;
      }
    };

    renderSedrEvidenceExplorer(panel, {
      files,
      readOnly: true,
      formatSize: formatFileSize,
      initialParentId: evidenceParentId,
      initialSearch: evidenceSearch,
      isBusy: () => evidenceBusy,
      onFolderChange: (parentId) => {
        evidenceParentId = parentId;
        pushAppUrl(buildGuestSedrRoomUrl(roomId, 'evidence', { evidenceFolderId: parentId }));
      },
      onSearchChange: (query) => {
        evidenceSearch = query;
      },
      onOpenFile: (file) => openFile(file, showEvidenceError),
      onDownloadFile: (file) => downloadFile(file, showEvidenceError),
      onNewFolder: async () => {},
      onUploadFiles: () => {},
    });

    tabContent.replaceChildren(panel);
    applyI18n(panel);
  };

  const renderAudit = async () => {
    const { tabContent } = layoutEls();
    const panel = document.createElement('div');
    panel.className = 'sedr-tab-panel sedr-audit-panel';
    const [events, categories, reqs, phases, roomFiles] = await Promise.all([
      guestApi.listAuditEvents(roomId),
      guestApi.listCategories(roomId).catch(() => []),
      requirements.length > 0
        ? Promise.resolve(requirements)
        : guestApi.listRequirements(roomId),
      phasesList.phases.length > 0
        ? Promise.resolve(phasesList)
        : guestApi.listPhases(roomId),
      files.length > 0
        ? Promise.resolve(files)
        : guestApi.listFiles(roomId),
    ]);
    requirements = reqs;
    phasesList = phases;
    files = roomFiles;
    seedSedrCategoriesCache(ctx!.roomId, categories);

    const lookup = buildSedrAuditLookup({
      requirements,
      phases: phasesList.phases,
      files,
      categories,
    });

    mountSedrAuditExpandableTable(panel, events, {
      lookup,
      onOpenRequirement: (requirementId) => {
        openCompliance({
          statusFilter: [],
          categoryFilter: 'all',
          requirementId,
          phaseId: null,
        });
      },
      onOpenPhase: (phaseId) => {
        openCompliance({
          statusFilter: [],
          categoryFilter: 'all',
          requirementId: null,
          phaseId,
        });
      },
      onOpenFile: (fileId) => {
        const file = files.find((f) => f.id === fileId);
        evidenceParentId = file?.parentId ?? null;
        openEvidence();
      },
    });
    tabContent.replaceChildren(panel);
  };

  const openCompliance = (opts?: {
    statusFilter?: SedrStatusFilter;
    categoryFilter?: SedrCategoryFilter;
    requirementId?: string | null;
    phaseId?: CompliancePhaseId;
  }) => {
    if (opts?.statusFilter !== undefined) complianceStatusFilter = opts.statusFilter;
    if (opts?.categoryFilter !== undefined) complianceCategoryFilter = opts.categoryFilter;
    if (opts?.requirementId !== undefined) {
      selectedRequirementId = opts.requirementId;
      selectedRequirementIds = selectedRequirementId ? [selectedRequirementId] : [];
      if (selectedRequirementId) {
        const selected = requirements.find((r) => r.id === selectedRequirementId);
        if (selected && isSedrLifecycleClosed(selected.status)) {
          complianceHideObsolete = false;
        }
      }
    }
    if (opts?.phaseId !== undefined) compliancePhaseId = opts.phaseId;
    compliancePanel = null;
    navigateRoom('compliance', { requirementId: selectedRequirementId });
  };

  const openAudit = () => {
    navigateRoom('audit');
  };

  const openEvidence = () => {
    navigateRoom('evidence', { evidenceFolderId: evidenceParentId });
  };

  const handleMetricSelect = (metric: SedrMetricKind) => {
    switch (metric) {
      case 'total':
        openCompliance({ statusFilter: [], categoryFilter: 'all', requirementId: null, phaseId: null });
        break;
      case 'evidence':
        openEvidence();
        break;
      case 'pendingApproval':
        openCompliance({ statusFilter: ['UnderReview'], categoryFilter: 'all', requirementId: null, phaseId: null });
        break;
      case 'approved':
        openCompliance({ statusFilter: ['ApprovedDischarged'], categoryFilter: 'all', requirementId: null, phaseId: null });
        break;
    }
  };

  const renderDashboard = () => {
    const { tabContent } = layoutEls();
    if (!ctx) return;
    const roomCtx = ctx;
    tabContent.replaceChildren(renderSedrDashboard({
      metrics: roomCtx.metrics,
      requirements,
      phasesList,
      auditEvents,
      canApprove: Boolean(roomCtx.canApprove),
      canManagePhases: false,
      onSelectMetric: handleMetricSelect,
      onOpenRequirement: (requirementId) => {
        openCompliance({ statusFilter: [], categoryFilter: 'all', requirementId, phaseId: null });
      },
      onViewAttentionAll: () => {
        openCompliance({
          statusFilter: roomCtx.canApprove ? ['UnderReview'] : ['PendingUpload', 'Denied'],
          categoryFilter: 'all',
          requirementId: null,
          phaseId: null,
        });
      },
      onSelectPhase: (phaseId) => {
        openCompliance({
          statusFilter: [],
          categoryFilter: 'all',
          requirementId: null,
          phaseId: phaseId === null ? 'unassigned' : phaseId,
        });
      },
      onViewAudit: openAudit,
    }));
  };

  const renderTab = async () => {
    switch (activeTab) {
      case 'dashboard':
        requirements = await guestApi.listRequirements(roomId);
        phasesList = await guestApi.listPhases(roomId);
        auditEvents = await guestApi.listAuditEvents(roomId);
        renderDashboard();
        break;
      case 'compliance':
        requirements = await guestApi.listRequirements(roomId);
        phasesList = await guestApi.listPhases(roomId);
        files = await guestApi.listFiles(roomId);
        {
          const categories = await guestApi.listCategories(roomId).catch(() => []);
          if (ctx) seedSedrCategoriesCache(ctx.roomId, categories);
        }
        renderCompliance();
        break;
      case 'evidence':
        files = await guestApi.listFiles(roomId);
        renderEvidence();
        break;
      case 'audit':
        await renderAudit();
        break;
    }
  };

  const mountRoomLayout = () => {
    gateSection.remove();
    page.className = 'module-page module-page--sedr sedr-room-page';
    page.innerHTML = `
      <div class="sedr-room-layout">
        <aside class="sedr-sidebar" id="sedr-sidebar"></aside>
        <div class="sedr-room-main files-browse-main">
          <div class="files-browse-chrome sedr-room-chrome">
            <div class="files-browse-chrome__identity">
              <h2 class="files-browse-chrome__title" id="sedr-room-title"></h2>
              <span class="sedr-room-chrome__org" id="sedr-room-org"></span>
              <span class="sedr-room-chrome__panel-slot" id="sedr-room-panel-slot"></span>
              <span id="sedr-room-banner-slot"></span>
            </div>
            <div class="files-browse-chrome__actions" id="sedr-room-actions"></div>
          </div>
          <div id="sedr-tab-content" class="sedr-room-main__body"></div>
        </div>
      </div>
    `;
  };

  const refreshRoom = async () => {
    ctx = await guestApi.roomContext(roomId);
    setGuestShellName(ctx.guestDisplayName, ctx.guestOrganization ?? null);
    if (!page.querySelector('.sedr-room-layout')) {
      mountRoomLayout();
    }
    renderSidebar();
    renderHeader();
    await renderTab();
  };

  const finishEntry = async () => {
    const { wrappedRoomKey } = await guestApi.getWrappedRoomKey(roomId);
    roomKey = await unwrapSedrRoomKey(wrappedRoomKey, roomId);
    await refreshRoom();
    verified = true;
  };

  const ensureTotpGate = async (): Promise<boolean> => {
    const session = await guestApi.session();
    setGuestShellName(session.displayName, session.organization);
    const roomSummary = await guestApi.getRoom(roomId);
    requireGuestTotp = roomSummary.requireGuestTotp || Boolean(ctx?.requireGuestTotp);

    if (requireGuestTotp && !session.totpEnrolled) {
      // Room entry should have gated this already; surface enroll UI without a busy overlay.
      try {
        const start = await guestApi.totpEnrollStart();
        showEnroll(start.secret, start.otpauthUri, start.pendingToken);
      } catch (cause) {
        showError(guestErrorKey(cause));
      }
      return false;
    }

    if (session.totpEnrolled && !session.totpVerified) {
      showChallenge();
      return false;
    }

    return true;
  };

  const beginWorkspace = async () => {
    if (entering) return;
    entering = true;
    errorEl.hidden = true;
    statusKey = 'sedr.guest.entering';
    statusEl.hidden = false;
    statusEl.className = 'muted';
    statusEl.textContent = t(statusKey);
    try {
      const totpReady = await ensureTotpGate();
      if (!totpReady) {
        statusEl.hidden = true;
        statusKey = null;
        return;
      }
      setCryptoBusy(gateSection, enrollForm, true, busyOptions);
      try {
        await finishEntry();
      } finally {
        if (!verified) {
          setCryptoBusy(gateSection, enrollForm, false, busyOptions);
        }
      }
    } catch (cause) {
      const code = (cause as { code?: string }).code;
      statusEl.hidden = true;
      statusKey = null;
      if (code === 'totp_enrollment_required') {
        await ensureTotpGate();
        return;
      }
      if (code === 'totp_verification_required') {
        showChallenge();
        return;
      }
      showError(guestErrorKey(cause));
    } finally {
      entering = false;
      if (!verified && statusKey === 'sedr.guest.entering') {
        statusEl.hidden = true;
        statusKey = null;
      }
    }
  };

  const submitTotp = async (code: string, mode: 'enroll' | 'challenge') => {
    if (entering) return;
    if (mode === 'enroll' && !pendingToken) return;
    entering = true;
    errorEl.hidden = true;
    const activeForm = mode === 'enroll' ? enrollForm : challengeForm;
    setCryptoBusy(gateSection, activeForm, true, busyOptions);
    try {
      if (mode === 'enroll') {
        await guestApi.totpEnrollConfirm(pendingToken!, code);
      } else {
        await guestApi.totpChallenge(code);
      }
      hideGateForms();
      await finishEntry();
    } catch (cause) {
      showError(guestErrorKey(cause));
    } finally {
      if (!verified) {
        setCryptoBusy(gateSection, activeForm, false, busyOptions);
      }
      entering = false;
    }
  };

  enrollForm.addEventListener('submit', (e) => {
    e.preventDefault();
    if (entering) return;
    const code = String(new FormData(enrollForm).get('totpCode') ?? '');
    void submitTotp(code, 'enroll');
  });

  challengeForm.addEventListener('submit', (e) => {
    e.preventDefault();
    if (entering) return;
    const code = String(new FormData(challengeForm).get('totpCode') ?? '');
    void submitTotp(code, 'challenge');
  });

  bindLocaleRefresh(() => {
    if (verified) {
      void refreshRoom();
      return;
    }
    applyI18n(page);
    if (errorKey && errorEl.isConnected && !errorEl.hidden) {
      errorEl.textContent = t(errorKey);
    }
    if (statusKey && statusEl.isConnected && !statusEl.hidden) {
      statusEl.textContent = t(statusKey);
    }
  });

  void beginWorkspace();

  const onSidebarMqChange = () => {
    if (!verified) return;
    renderSidebar();
    updateRoomHelpTip();
  };
  sidebarMq.addEventListener('change', onSidebarMqChange);

  const observer = new MutationObserver(() => {
    if (!page.isConnected) {
      page.removeEventListener('guest-sedr:room-nav', onRoomNav);
      sidebarMq.removeEventListener('change', onSidebarMqChange);
      chromeActionsCleanup?.();
      observer.disconnect();
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });

  return page;
}
